import { Collection, HandlersCollection, RefString } from "@tblabs/truffle";
import { Step } from "../Steps/Step/Step";
import { StepEvent } from "../Steps/Step/StepEvent";
import { Human } from "./Human";
import { OrderMeta } from "./OrderMeta";
import { StepData } from "../Steps/Step/StepData";
import { BasketItem } from "./Basket/BasketItem";
import { AddToBasketStep } from "../Steps/AddToBasket/AddToBasketStep";
import { RentBasketItem } from "./Basket/RentBasketItem";
import { Cash } from "../Steps/Step/Cash";
import { CashType } from "../Steps/Step/CashType";


export class Order
{
    public Id: string = "";
    public Steps = new Collection<Step<any>>();
    public People = new Collection<Human>();
    public Meta = new OrderMeta();
    public Notes = {
        Public: new RefString(""),
        Private: new RefString("")
    }

    public get Status(): string
    {
        if (this.Meta.IsNew) return "Nowe";
        if (this.IsClosed) return "Zamknięte";
        if (this.IsDone) return "Zakończone";
        if (this.IsFailed) return "Problematyczne";
        return "W trakcie realizacji";
    }

    public get CurrentStep(): Step<any> | undefined
    {
        return this.Steps.Items.find(x => x.IsInProgress);
    }

    public get IsClosed(): boolean
    {
        return this.Meta.Folder === "Closed";
    }

    public get IsFailed(): boolean
    {
        return this.Steps.Items.some(x => x.IsFailedDone);
    }

    public get IsDone(): boolean
    {
        return this.Steps.Items.every(x => x.IsDone);
    }
    
    public get IsOpen(): boolean
    {
        return this.Steps.Items.some(x => x.IsInProgress);
    }

    private onChange = new HandlersCollection<(step: Step<any>, data: StepData) => void>();
    public OnChange(handler: (step: Step<any>, data: StepData) => void): void
    {
        this.onChange.Add(handler);
    }

    constructor(id?: string)
    {
        if (id)
        {
            this.Id = id;
        }
    }

    public get PeopleAsString(): string
    {
        return this.People.Map(x => x.ExperienceIcon + " " + x.Name.value + ", tel. " + x.Phone.value).join(", ");
    }

    public get BasketItems(): BasketItem[]
    {
        return this.Steps.Items
            .filter(x => x instanceof AddToBasketStep)
            .map(x => x.Data.BasketItems)
            .flatMap(x => x.Items);
    }

    public get BasketValue(): number
    {
        return this.BasketItems.reduce((acc, x) => acc + x.TotalPrice.value, 0);
    }

    public get DesiredDeposit(): number
    {
        return this.BasketItems.filter(x => x instanceof RentBasketItem).reduce((acc, x) => acc + x.Deposit.value, 0);
    }

    public Mana(filter?: CashType[]): Cash[]
    {
        let mana = this.Steps.Items.flatMap(x => x.Mana)

        mana = mana.filter(x => filter ? filter.includes(x.Type) : true);

        return mana;
    }

    public get Expenses(): number
    {
        return this.Mana(["Expense"]).reduce((acc, y) => acc + y.Amount, 0);
    }

    public get ReceivedIncome(): number
    {
        return this.Mana(["ReceivedIncome"]).reduce((acc, y) => acc + y.Amount, 0);
    }

    public get ReceivedDeposit(): number
    {
        return this.Mana(["ReceivedDeposit"]).reduce((acc, y) => acc + y.Amount, 0);
    }

    public get ReturnedDeposit(): number
    {
        return this.Mana(["ReturnedDeposit"]).reduce((acc, y) => acc + y.Amount, 0);
    }

    public get Profit(): number
    {
        return this.ReceivedIncome - this.Expenses + (this.ReceivedDeposit - this.ReturnedDeposit);
    }

    public SelfSetup()
    {
        this.Steps
            .OnChange((step) =>
            {
                this.onChange.Call(step, undefined);
            })
            .ForEach((step) =>
            {
                step.Order = this;

                step.OnStateChange((data) =>
                {
                    this.onChange.Call(step, data);
                });
            });
    }

    public get ShortId(): string
    {
        return this.Id.slice(-5).toUpperCase();
    }

    public get AsEvents(): StepEvent[]
    {
        return this.Steps.Items.flatMap(x => x.AsEvents);
    }

    public get SearchBy(): string
    {
        const orderSearchBy = this.ShortId;
        const searchInSteps = this.Steps.Items.map(x => x.Data.SearchBy).join(", ");
        const peopleSearchBy = this.People.Map(x => x.Name.value + " " + x.Phone.value).join(", ");

        const searchBy = orderSearchBy + " " + searchInSteps + " " + peopleSearchBy;

        return searchBy;
    }
}
